import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    useId,
    useTransition,
    type ChangeEvent,
    type FC,
    type MouseEvent,
    type ReactNode,
} from 'react';
import { graphql, useFragment, useRefetchableFragment } from 'react-relay';
import { useIntl } from 'dibs-react-intl';
import classnames from 'classnames';
import { SbSharedRefineMenuTypeaheadControls } from './SbSharedRefineMenuTypeaheadControls.jsx';
import { SbSharedRefineMenuSearchInput } from '../SbSharedRefineMenuSearchInput/SbSharedRefineMenuSearchInput.tsx';
import { SbSharedRefineMenuSearchResults } from '../SbSharedRefineMenuSearchResults/SbSharedRefineMenuSearchResults.tsx';
import { useDispatch } from 'react-redux';
import { updateUriRef } from '../../../actions/filterActions.ts';
import { decode64 } from '../../../utils/decode.ts';
import { trackFacetSearchClick } from '../../../utils/tracking/searchBrowse/filterTracking.ts';
import { useSbSelector } from '../../../reducers/useSbSelector.ts';
import { getUserSessionCountryCode } from 'dibs-regional-info/exports/regionalInfoHelpers.ts';
import { getFilterValues } from '../sbSharedRefineMenuHelpers.ts';
import { useDebouncedCallback } from 'dibs-react-hooks/exports/useDebouncedCallback.js';
import { sbMessages } from '../../sbMessages.tsx';
import { type FilterValue } from '../SbSharedRefineMenuMultiSelect/SbSharedRefineMenuMultiSelect.tsx';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy.ts';

import styles from './SbSharedRefineMenuTypeaheadContainer.module.css';
import dibsCss from 'dibs-css';

import { type SbSharedRefineMenuTypeaheadContainer_viewer$key } from './__generated__/SbSharedRefineMenuTypeaheadContainer_viewer.graphql.ts';
import { type SbSharedRefineMenuTypeaheadContainer_itemSearch$key } from './__generated__/SbSharedRefineMenuTypeaheadContainer_itemSearch.graphql.ts';

const THRESHOLD_MIN_SEARCH_STRING_LENGTH = 3;
const THRESHOLD_FETCH_DATA_DEBOUNCE = 250;

const refetchableViewerFragment = graphql`
    fragment SbSharedRefineMenuTypeaheadContainer_viewer on Viewer
    @refetchable(queryName: "SbSharedRefineMenuTypeaheadContainerQuery")
    @argumentDefinitions(
        uriRef: { type: "String" }
        searchString: { type: "String" }
        facetName: { type: "String" }
        hasQuery: { type: "Boolean", defaultValue: false }
        userCountryCode: { type: "String", defaultValue: "" }
    ) {
        itemFacetSearch(
            searchString: $searchString
            uriRef: $uriRef
            facetName: $facetName
            countryCode: $userCountryCode
        ) @include(if: $hasQuery) {
            ...SbSharedRefineMenuSearchResults_itemFacetSearch
            edges {
                node {
                    urlLabel
                    linkReference
                    displayName
                    count
                }
            }
        }
    }
`;
const itemSearchFragment = graphql`
    fragment SbSharedRefineMenuTypeaheadContainer_itemSearch on ItemSearchQueryConnection {
        topCategoryL1
        appliedFilters {
            name
            canBeDismissed
            clearAllLinkReference
            values {
                urlLabel
                linkReference
                count
                displayName
            }
        }
    }
`;

const isFilterInList = <T extends { urlLabel: string | null }, V extends T>(
    filterValue: T,
    list: readonly V[]
): boolean => {
    return list.findIndex(value => value.urlLabel === filterValue.urlLabel) > -1;
};

type Props = {
    viewer: SbSharedRefineMenuTypeaheadContainer_viewer$key;
    itemSearch: SbSharedRefineMenuTypeaheadContainer_itemSearch$key;
    availableFilterValues: FilterValue[];
    children: (props: { valuesToShow: FilterValue[] }) => ReactNode;
    collapsedItemCount?: number;
    filterName: string;
    iconMap?: Record<string, FC<{ className?: string }>>;
    showSearchBar?: boolean;
    trackingOptions?: Record<string, unknown>;
};

export const SbSharedRefineMenuTypeaheadContainer: FC<Props> = ({
    viewer: viewerRef,
    itemSearch: itemSearchRef,
    availableFilterValues,
    children,
    collapsedItemCount = 5,
    filterName,
    iconMap,
    showSearchBar,
    trackingOptions,
}) => {
    const [viewer, refetch] = useRefetchableFragment(refetchableViewerFragment, viewerRef);
    const itemSearch = useFragment(itemSearchFragment, itemSearchRef);
    const [isTypeaheadActive, setIsTypeaheadActive] = useState(false);
    const dispatch = useDispatch();
    const intl = useIntl();
    const htmlId = useId();
    const [searchString, setSearchString] = useState('');
    const [hideResults, setHideResults] = useState(true);
    const [ariaLive, setAriaLive] = useState<'off' | 'polite'>('off');
    const [isLoading, startTransition] = useTransition();
    const updateAfterRefetch = useRef(false);
    const [initialValuesHeight, setInitialValuesHeight] = useState<number | null>(null);

    const { itemFacetSearch } = viewer;
    const { appliedFilters } = itemSearch;
    const clearAllLinkReference = (appliedFilters || []).find(
        filter => filter?.name === filterName
    )?.clearAllLinkReference;

    const { generatedUriRef, refetchInFlight } = useSbSelector(state => state.filters);

    useEffect(() => {
        if (!refetchInFlight && updateAfterRefetch.current) {
            updateAfterRefetch.current = false;
            setSearchString('');
        }
    }, [refetchInFlight]);

    const clearSearchString = useCallback(() => {
        updateAfterRefetch.current = true;
    }, []);

    const setInitialValuesRef = useCallback(
        (node: HTMLElement | null) => {
            if (!node || initialValuesHeight !== null) {
                return;
            }

            const height = Math.ceil(node.getBoundingClientRect().height);
            if (height) {
                setInitialValuesHeight(height);
            }
        },
        [initialValuesHeight]
    );

    const { appliedFilterValues, excludedDisplayNames } = useMemo(() => {
        const appliedFilterVals = getFilterValues(appliedFilters, filterName);
        const excludedNames = appliedFilterVals.map(value => value.displayName).filter(filterFalsy);
        return { appliedFilterValues: appliedFilterVals, excludedDisplayNames: excludedNames };
    }, [appliedFilters, filterName]);

    const toggleTypeahead = useCallback(() => {
        if (!isTypeaheadActive) {
            trackFacetSearchClick(filterName);
        }
        setIsTypeaheadActive(prev => !prev);
    }, [isTypeaheadActive, filterName]);

    const handleToggleClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            event.preventDefault();
            toggleTypeahead();
        },
        [toggleTypeahead]
    );

    const clearFilters = useCallback(() => {
        if (clearAllLinkReference) {
            dispatch(updateUriRef({ uriRef: decode64(clearAllLinkReference), trackingOptions }));
        }
    }, [dispatch, clearAllLinkReference, trackingOptions]);

    const handleClearClick = useCallback(
        (event: MouseEvent<HTMLElement>) => {
            event.preventDefault();
            clearFilters();
        },
        [clearFilters]
    );

    const handleResultClick = useCallback(
        (filterValue: FilterValue, event: MouseEvent<HTMLElement>) => {
            dispatch(
                updateUriRef({
                    filterName,
                    filterValue,
                    isFromTypeahead: true,
                    event,
                    trackingOptions,
                })
            );
            toggleTypeahead();
            setSearchString('');
        },
        [dispatch, filterName, toggleTypeahead, trackingOptions]
    );

    const fetchSearchData = useCallback(
        (searchValue: string) =>
            startTransition(() => {
                refetch(
                    {
                        uriRef: generatedUriRef,
                        hasQuery: true,
                        searchString: searchValue,
                        facetName: filterName,
                        userCountryCode: getUserSessionCountryCode(),
                    },
                    { onComplete: () => setHideResults(false) }
                );
            }),
        [filterName, generatedUriRef, refetch]
    );

    const [debouncedFetchSearchData] = useDebouncedCallback(
        fetchSearchData,
        THRESHOLD_FETCH_DATA_DEBOUNCE
    );

    const handleInput = useCallback(
        (event: ChangeEvent<HTMLInputElement>) => {
            const { value: searchValue } = event.target;
            if (searchValue !== '' && searchValue.length >= THRESHOLD_MIN_SEARCH_STRING_LENGTH) {
                debouncedFetchSearchData(searchValue);
            } else {
                setHideResults(true);
            }
            setSearchString(searchValue);
        },
        [debouncedFetchSearchData]
    );

    /**
     * We never show more available filters that are defined in collapsedItemCount, so we
     * can discard everything else.
     */
    const showClearButton = !!appliedFilterValues.length;

    let valuesToShow = appliedFilterValues.reduceRight(
        (result, value, index) => {
            /**
             * Check if the filter value is not present in the suggested/available filter
             * values.
             */
            if (result.length && !isFilterInList(value, result)) {
                /**
                 * If we're here - this means the value was probably applied from the
                 * typeahead or it's a value that no longer fits in the available values and should
                 * be moved to the top.
                 * Find filter in availableFilterValues array to ensure the correct counts are used
                 */
                const availableFilter = availableFilterValues.find(
                    filter => filter.urlLabel === value.urlLabel
                );
                result.unshift(availableFilter || value);
                /**
                 * It's possible that we will have more applied values than the number specified in
                 * `collapsedItemCount`, so we need to guard against removing them.
                 */
                if (index < collapsedItemCount) {
                    result.pop();
                }
            }

            return result;
        },
        availableFilterValues.slice(0, collapsedItemCount)
    );

    if (isTypeaheadActive) {
        valuesToShow = valuesToShow.filter(value => isFilterInList(value, appliedFilterValues));
    }

    const showTypeAheadControls = !isTypeaheadActive;

    const itemFacetSearchValues = (itemFacetSearch?.edges?.map(edge => edge?.node) || []).filter(
        (filterValue): filterValue is NonNullable<typeof filterValue> =>
            filterFalsy(filterValue) && !isFilterInList(filterValue, appliedFilterValues)
    );
    const showSearchResults =
        searchString.length >= THRESHOLD_MIN_SEARCH_STRING_LENGTH &&
        itemFacetSearchValues.length > 0;
    const noSearchResults =
        searchString.length >= THRESHOLD_MIN_SEARCH_STRING_LENGTH &&
        !isLoading &&
        itemFacetSearch?.edges &&
        itemFacetSearchValues.length === 0;

    return showSearchBar ? (
        <>
            <div className={dibsCss.overflowAuto}>
                <SbSharedRefineMenuSearchInput
                    ariaControls={htmlId}
                    className={classnames(
                        dibsCss.mx0,
                        dibsCss.mt0,
                        dibsCss.mbSmall,
                        dibsCss.textNoir,
                        dibsCss.bgWhite
                    )}
                    onSubmit={event => event?.preventDefault()}
                    placeholder={intl.formatMessage(sbMessages.search)}
                    onInput={handleInput}
                    value={searchString}
                    filterName={filterName}
                    onBlur={() => setAriaLive('off')}
                    onFocus={() => setAriaLive('polite')}
                />
                <div
                    ref={setInitialValuesRef}
                    style={
                        initialValuesHeight ? { minHeight: `${initialValuesHeight}px` } : undefined
                    }
                >
                    {noSearchResults && (
                        <p
                            className={classnames(
                                dibsCss.textBodySmallNormal,
                                dibsCss.textSatan,
                                dibsCss.mbSmall
                            )}
                        >
                            {intl.formatMessage(sbMessages.noResultsFound)}
                        </p>
                    )}
                    {showSearchResults && (
                        <div onClickCapture={clearSearchString}>
                            {children({
                                valuesToShow: [...appliedFilterValues, ...itemFacetSearchValues],
                            })}
                        </div>
                    )}
                    {!(noSearchResults || showSearchResults) && children({ valuesToShow })}
                </div>
            </div>
            <p
                className={classnames(
                    styles.findMoreMessage,
                    dibsCss.textBodySmallNormal,
                    dibsCss.textSatan,
                    dibsCss.mbSmall,
                    { [dibsCss.opacity0]: searchString.length }
                )}
            >
                {intl.formatMessage(sbMessages.searchToFindMore)}
            </p>
        </>
    ) : (
        <>
            {children({ valuesToShow })}
            {isTypeaheadActive && (
                <div
                    className={classnames(dibsCss.textIron, dibsCss.overflowAuto, {
                        [dibsCss.mtMedium]: valuesToShow.length,
                    })}
                >
                    <SbSharedRefineMenuSearchInput
                        ariaControls={htmlId}
                        className={classnames(
                            dibsCss.mx0,
                            dibsCss.mt0,
                            dibsCss.mbSmall,
                            dibsCss.textNoir,
                            dibsCss.bgWhite
                        )}
                        onSubmit={event => event?.preventDefault()}
                        placeholder={intl.formatMessage(sbMessages.search)}
                        onInput={handleInput}
                        value={searchString}
                        filterName={filterName}
                        onBlur={() => setAriaLive('off')}
                        onFocus={() => setAriaLive('polite')}
                    />
                    <SbSharedRefineMenuSearchResults
                        id={htmlId}
                        ariaLive={ariaLive}
                        itemFacetSearch={hideResults ? null : itemFacetSearch}
                        searchString={searchString}
                        isLoading={isLoading}
                        excludedDisplayNames={excludedDisplayNames}
                        onResultClick={handleResultClick}
                        iconMap={iconMap}
                    />
                    <SbSharedRefineMenuTypeaheadControls
                        showSearch={false}
                        onToggleClick={handleToggleClick}
                        filterName={filterName}
                    />
                </div>
            )}
            {showTypeAheadControls && (
                <SbSharedRefineMenuTypeaheadControls
                    filterName={filterName}
                    categoryL1={itemSearch.topCategoryL1 || ''}
                    showSearch
                    showClear={showClearButton}
                    onToggleClick={handleToggleClick}
                    onClearClick={handleClearClick}
                />
            )}
        </>
    );
};
